import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { X, Copy, Loader2, Mail, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminDrawer = ({ open, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [blast, setBlast] = useState("idle");
  const [blastResult, setBlastResult] = useState(null);

  const sendBlast = async () => {
    if (blast === "idle") {
      setBlast("confirm");
      return;
    }
    setBlast("sending");
    setBlastResult(null);
    try {
      const { data: res } = await axios.post(`${API}/subscribers/blast`);
      setBlastResult(
        `Sent to ${res.sent} subscriber${res.sent === 1 ? "" : "s"}` +
          (res.failed?.length ? ` — ${res.failed.length} failed` : "")
      );
      toast.success("Launch blast sent");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Blast failed to send");
    }
    setBlast("idle");
  };

  useEffect(() => {
    if (!open) return;
    setBlast("idle");
    setBlastResult(null);
    setLoading(true);
    axios
      .get(`${API}/subscribers`)
      .then((res) => setData(res.data))
      .catch(() => toast.error("Could not load subscribers"))
      .finally(() => setLoading(false));
  }, [open]);

  const copyAll = () => {
    if (!data?.subscribers?.length) return;
    navigator.clipboard.writeText(
      data.subscribers.map((s) => s.email).join("\n")
    );
    toast.success("All emails copied to clipboard");
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[70] bg-slate-900/20 backdrop-blur-sm"
          />
          <motion.aside
            data-testid="admin-modal-content"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="fixed right-0 top-0 z-[80] flex h-full w-full max-w-md flex-col border-l border-slate-200 bg-white shadow-2xl"
          >
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-5">
              <div>
                <h3 className="font-display text-lg font-bold text-slate-900">
                  Launch List
                </h3>
                <p className="font-mono text-[11px] uppercase tracking-widest text-slate-400">
                  {data ? `${data.count} subscriber${data.count === 1 ? "" : "s"}` : "Loading"}
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  data-testid="copy-emails-button"
                  onClick={copyAll}
                  className="flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-slate-600 transition-colors duration-300 hover:border-skybrand hover:text-skybrand"
                >
                  <Copy size={12} /> Copy all
                </button>
                <button
                  data-testid="admin-modal-close"
                  onClick={onClose}
                  aria-label="Close subscribers panel"
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition-colors duration-300 hover:border-kinetic hover:text-kinetic"
                >
                  <X size={15} />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4" data-lenis-prevent>
              {loading ? (
                <div className="flex h-40 items-center justify-center">
                  <Loader2 size={22} className="animate-spin text-kinetic" />
                </div>
              ) : !data?.subscribers?.length ? (
                <div className="flex h-40 flex-col items-center justify-center gap-3 text-slate-400">
                  <Mail size={22} />
                  <p className="font-mono text-[11px] uppercase tracking-widest">
                    No subscribers yet
                  </p>
                </div>
              ) : (
                <table data-testid="subscribers-table" className="w-full">
                  <tbody>
                    {data.subscribers.map((s, i) => (
                      <tr key={s.email} className="border-b border-slate-100 last:border-0">
                        <td className="py-3.5 pr-3 font-mono text-[11px] text-slate-300">
                          {String(i + 1).padStart(2, "0")}
                        </td>
                        <td className="py-3.5 text-sm font-medium text-slate-800">
                          {s.email}
                        </td>
                        <td className="py-3.5 text-right font-mono text-[10px] uppercase text-slate-400">
                          {new Date(s.subscribed_at).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                          })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="border-t border-slate-200 px-6 py-4">
              {blastResult && (
                <p
                  data-testid="blast-result"
                  className="mb-3 font-mono text-[11px] uppercase tracking-widest text-skybrand-hover"
                >
                  {blastResult}
                </p>
              )}
              <button
                data-testid="send-blast-button"
                onClick={sendBlast}
                disabled={blast === "sending" || !data?.count}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-kinetic px-6 py-3.5 font-display text-xs font-bold uppercase tracking-wide text-white transition-colors duration-300 hover:bg-kinetic-hover disabled:opacity-50"
              >
                {blast === "sending" ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Send size={14} />
                )}
                {blast === "confirm"
                  ? `Confirm — email ${data?.count ?? 0} subscriber${data?.count === 1 ? "" : "s"}`
                  : blast === "sending"
                    ? "Sending..."
                    : "Send Launch Blast"}
              </button>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
